import DefaultSemiBoldFont from './../fonts/OpenSans/OpenSans-SemiBold.ttf';
import DefaultBoldFont from './../fonts/OpenSans/OpenSans-Bold.ttf';
import DefaultRegularFont from './../fonts/OpenSans/OpenSans-Regular.ttf';
import ABCReproRegularFont from './../fonts/ABCRepro/ABCRepro-Screen.woff2';
import ABCReproBoldFont from './../fonts/ABCRepro/ABCRepro-Bold.woff2';
import NittiGroteskRegularFont from './../fonts/NittiGrotesk/NNNittiGrotesk-TextRegular.woff2';
import NittiGroteskBoldFont from './../fonts/NittiGrotesk/NNNittiGrotesk-TextBold.woff2';
import { css } from '@emotion/react';

const spaces = [0, 4, 8, 16, 24, 32, 40, 48, 56, 64];
const breakpoints = ['0px', '600px', '900px', '1200px', '1800px'];

breakpoints.mobile = breakpoints[0];
breakpoints.tablet = breakpoints[1];
breakpoints.desktop = breakpoints[2];

breakpoints.sm = breakpoints[0]; // phone only
breakpoints.md = breakpoints[1]; // tablet
breakpoints.ml = breakpoints[2]; // tablet landscape
breakpoints.lg = breakpoints[3]; // desktop
breakpoints.xl = breakpoints[4]; // big desktop

const colors = {
  black: [
    ['#2F3033'], //title text, menu icon, menu text
  ],
  white: [
    ['#FFFFFF'], // container background
  ],
  grey: [
    ['#4E5056'], // text
    [
      '#A0B3BA', // secondary button, dotted borders, secondary text
      '#6B787D', // secondary button hover
      '#68797F', // secondary button active
    ],
    ['#D3DDE0'], // container border, inactive component
    [
      '#EFF2F4', // background
      '#FAFAFA', // modal sec background
    ],
  ],
  blue: [
    [
      '#43A3D0', // active icon, title icon, link text
      '#D9ECF5', // active menu item background
      '#ECF5FA', // menu dropdown background, add button background, file upload background
    ],
  ],
  orange: [
    [
      '#F7A91B', // buttons, page nav active, notification dot
      '#FF8F00', // Button active
      '#FFB115', // Button hover
    ],
  ],
  warning: {
    background: '#fee7ce',
    foreground: '#cb5500',
  },
  red: [
    [
      '#EB5050', // Alert, failed, denied, delete button
      '#DD4343', // Delete button active
      '#E85D5E', // Delete button hover
    ],
    ['#EF4C56'], // logo/loader
  ],
  green: [
    ['#57C46C'], // Success
    ['#D4EDE3'], // Success background
  ],
};

export const UwKluisTheming = {
  black: colors.black[0][0],
  primaryFont: colors.grey[0][0],
  secondaryFont: colors.grey[1][0],
  secondaryButtonHover: colors.grey[1][1],
  secondaryButtonActive: colors.grey[1][2],
  border: colors.grey[2][0],
  background: colors.grey[3][0],
  secondaryBackground: colors.grey[3][1],
  white: colors.white[0][0],
  primary: colors.blue[0][0],
  primaryActive: colors.blue[0][1],
  primaryBackground: colors.blue[0][2],
  secondary: colors.orange[0][0],
  secondaryHover: colors.orange[0][2],
  alert: colors.red[0][0],
  alertActive: colors.red[0][1],
  alertHover: colors.red[0][2],
  warningBackground: colors.warning.background,
  warningForeground: colors.warning.foreground,
  succes: colors.green[0][0],
  succesBackground: colors.green[1][0],
  loader: colors.red[1][0],
  // Dynamically set based on tenant - see WhiteLabelContext
  primaryContrast : undefined,
};

export const createThemedBorders = (colors) => ({
  default: `1px solid ${colors.border}`,
  dashed: `2px dashed ${colors.border}`,
  modal: `4px solid ${colors.primary}`,
});


const fontSizes = ['0.75em', '0.875em', '1rem', '1.25em', '1.5rem', '1.75rem'];
const fontWeights = [400, 600, 700, 800];

const theme = {
  breakpoints,
  space: {
    none: spaces[0], // 0
    xs: spaces[1], // 4
    s: spaces[2], // 8
    m: spaces[3], // 16
    l: spaces[4], // 24
    xl: spaces[5], // 32
    '2xl': spaces[6], // 40
    '3xl': spaces[7], // 48
    '4xl': spaces[8], // 56
    '5xl': spaces[9], // 64
  },

  fontSizes: {
    xs: fontSizes[0], // 12
    s: fontSizes[1], // 14
    m: fontSizes[2], // 16
    l: fontSizes[3], // 20
    xl: fontSizes[4], // 24
    '2xl': fontSizes[5], // 28
  },

  fontWeights: {
    thin: fontWeights[0], // 400
    normal: fontWeights[1], // 600
    bold: fontWeights[2], // 700
    bolder: fontWeights[3], // 800
  },

  fontScales: {
    xs: {
      fontSize: fontSizes[0],
      lineHeight: 1.333, //16px(uit design) % 12px(fontSize),
    },
    s: {
      fontSize: fontSizes[1], // 14px
      lineHeight: 1.4,
    },
    m: {
      fontSize: fontSizes[2], // 16px
      lineHeight: 1.5,
    },
    l: {
      fontSize: fontSizes[3], // 20px
      lineHeight: 1.25,
    },
    xl: {
      fontSize: fontSizes[4], // 24px
      lineHeight: 1.2,
    },
    '2xl': {
      fontSize: fontSizes[5], // 28px
      lineHeight: 1.17,
    },
  },

  font: 'Open Sans',
  borderRadius: '4px',
  buttonFontWeight: fontWeights[2],
  cardStyleOverwrite: {},
  colors: UwKluisTheming,
  borders: createThemedBorders(UwKluisTheming),

  borderImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='5' ry='5' stroke='%23BDBDBDFF' stroke-width='4' stroke-dasharray='0%2c 12' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e")`,
  backgroundImage: `url(/images/background/pattern.svg)`,

  buttons: {
    symantic: {
      bg: 'inherit',
      fontSize: 'inherit',
      fontFamily: 'inherit',
      border: 0,
      padding: 0,
      margin: 0,
    },
    primary: {
      color: 'white',
      bg: 'secondary',
      '&:hover': {
        bg: 'secondaryHover',
      },
    },
    'primary-outline': {
      color: 'secondary',
      bg: 'transparent',
      border: '1px solid',
      borderColor: 'secondary',
      '&:hover': {
        color: 'white',
        bg: 'secondaryHover',
        borderColor: 'secondaryHover',
      },
    },
    secondary: {
      color: 'primaryFont',
      bg: 'secondaryFont',
      '&:hover': {
        bg: 'secondaryButtonHover',
      },
    },
    'secondary-outline': {
      color: 'secondaryFont',
      bg: 'transparent',
      border: '1px solid',
      borderColor: 'secondaryFont',
      '&:hover': {
        color: 'primaryFont',
        bg: 'secondaryButtonHover',
        borderColor: 'secondaryButtonHover',
      },
    },
    alert: {
      color: 'white',
      bg: 'alert',
      '&:hover': {
        bg: 'alertHover',
      },
    },
    'alert-outline': {
      color: 'alert',
      bg: 'transparent',
      border: '1px solid',
      borderColor: 'alert',
      '&:hover': {
        color: 'white',
        bg: 'alertHover',
        borderColor: 'alertActive',
      },
    },
    action: {
      color: 'primary',
      bg: 'primaryActive',
      '&:hover': {
        bg: 'primaryBackground',
      },
    },
    'action-outline': {
      color: 'primaryFont',
      bg: 'transparent',
      border: '1px solid',
      borderColor: 'primary',
    },
    disabled: {
      color: 'grey',
      'text-decoration': 'none !important',
    },
    anchor: {
      color: 'secondary',
      height: 'auto',
      lineHeight: '1rem !important',
      fontWeight: 'normal',
      bg: 'transparent',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },

  buttonSizes: {
    none: {
      padding: 0,
    },
    tiny: {
      padding: '2px 6px',
    },
    small: {
      padding: '8px 16px',
    },
    medium: {
      padding: '12px 24px',
    },
  },

  boxShadows: {
    normal: '0 0 8px 0 rgba(0, 0, 0, 0.12)',
    large: '0 0 24px 0 rgba(0, 0, 0, 0.12)',
  },
};

export default theme;


export const globalStyles = theme => css`
  //  Default
  @font-face {
      font-family: 'Open Sans';
      src: url('${DefaultRegularFont}');
      font-weight: ${theme.fontWeights.normal};
  }
  @font-face {
      font-family: 'Open Sans';
      src: url('${DefaultSemiBoldFont}');
      font-weight: ${theme.fontWeights.bold};
  }
  @font-face {
      font-family: 'Open Sans';
      src: url('${DefaultBoldFont}');
      font-weight: ${theme.fontWeights.bolder};
  }
  //  Argo
  @font-face {
      font-family: 'ABC Repro';
      src: url('${ABCReproRegularFont}');
      font-weight: ${theme.fontWeights.normal};
  }
  @font-face {
      font-family: 'ABC Repro';
      src: url('${ABCReproBoldFont}');
      font-weight: ${theme.fontWeights.bold};
  }
  //  NN
  @font-face {
      font-family: 'Nitti Grotesk';
      src: url('${NittiGroteskRegularFont}');
      font-weight: ${theme.fontWeights.normal};
  }
  @font-face {
      font-family: 'Nitti Grotesk';
      src: url('${NittiGroteskBoldFont}');
      font-weight: ${theme.fontWeights.bold};
  }

  html {
    padding: ${theme.space.none};
    margin: ${theme.space.none};
    box-sizing: border-box;
  }

  body {
    padding: 0px;
    padding: ${theme.space.none};
    margin: ${theme.space.none};
    font-family: ${theme.font};
    line-height: 1.5;
    color: ${theme.colors.primaryFont};

    // Catch the transistion between desktop-breakpoints
    @media (max-width: 899px) {
        &.ReactModal__Body--open, &.mobile-menu--open {
            overflow: hidden;
        }
    }

    ul {
      list-style: none;
    }
    a {
      text-decoration: none;
      color: inherit;
    }

    h2 {
      line-height: 1.2;
    }
    h1 {
      line-height: 1.17;
    }

    * {
      box-sizing: border-box;
      font-family: ${theme.font};
    }
  }
`;

